/**
 * Item #14 — the shop floor as the Guided Start bay selector.
 *
 * Draws the facility's bays and parking spots (the Birdseye geometry) with the
 * Birdseye camera (useMapCamera: drag, wheel, one-finger pan, pinch, fit).
 *
 *   bays     the technician's choices: Free (tappable), Occupied / Just refused
 *            (shown, never selectable), Planned (marked). A laid-out bay that
 *            is not a choice (locked out, inactive) is drawn greyed, as context.
 *   spots    context only — never selectable here.
 *
 * Returns a BAY ID through `onSelect` and writes nothing: the selection goes
 * to the same commitGuidedStart(bayId) → pull_in_and_start_my_timer path as
 * the list. Without `onSelect` the map only shows (the countdown's preview).
 *
 * Touch: a pan that starts on a bay pans (it never selects); only a tap does.
 * Occupied bays use aria-disabled, not `disabled`, so a pan can start on them.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMapCamera, type MapRect } from '@/hooks/useMapCamera';
import { minTapZoom, type FacilityLayout } from '@/lib/location/facilityLayout';
import type { GuidedBayChoice } from '@/lib/timer/interaction/guidedStart';

export type MapBayState = 'free' | 'occupied' | 'refused' | 'planned' | 'selected';

/** The one state a bay shows; selection wins, then the reasons it can't be chosen. */
export function mapBayState(c: GuidedBayChoice, selectedBayId: string | null): MapBayState {
  if (c.occupied) return 'occupied';
  if (c.refused) return 'refused';
  if (selectedBayId === c.id) return 'selected';
  return c.planned ? 'planned' : 'free';
}

const STATE_WORDS: Record<MapBayState, string> = {
  free: 'Free',
  occupied: 'Occupied',
  refused: 'Just refused',
  planned: 'Planned',
  selected: 'Selected',
};

const STATE_CLASS: Record<MapBayState, string> = {
  free: 'border-emerald-600 bg-emerald-50 text-emerald-950 dark:bg-emerald-950/40 dark:text-emerald-50',
  planned: 'border-primary bg-primary/10 ring-2 ring-primary ring-offset-1',
  selected: 'border-primary bg-primary text-primary-foreground ring-2 ring-primary ring-offset-1',
  occupied: 'cursor-not-allowed border-muted-foreground/40 bg-muted text-muted-foreground',
  refused: 'cursor-not-allowed border-destructive/60 bg-destructive/10 text-muted-foreground',
};

/** Screen margin (px) around the framed bays or floor. */
export const FIT_MARGIN_PX = 16;

/** Presses here never start a pan (the map's own controls). */
const isMapControl = (t: HTMLElement) => !!t.closest('[data-map-control]');

export interface FacilityPickerMapProps {
  layout: FacilityLayout;
  choices: readonly GuidedBayChoice[];
  selectedBayId: string | null;
  /** Absent: show only (no bay is selectable). */
  onSelect?: (bayId: string) => void;
  /** Kept in view when the fit has to zoom in (the planned bay). */
  focusBayId?: string | null;
  disabled?: boolean;
  className?: string;
}

export function FacilityPickerMap({
  layout,
  choices,
  selectedBayId,
  onSelect,
  focusBayId = null,
  disabled = false,
  className,
}: FacilityPickerMapProps) {
  const items = useMemo<MapRect[]>(
    () => [...layout.bays, ...layout.spots].map((r) => ({ x: r.x, y: r.y, w: r.w, h: r.h })),
    [layout],
  );
  const camera = useMapCamera({ items, ignorePanStart: isMapControl });
  const { containerRef, zoom, offset, bounds, isDragging, fitToScreen } = camera;
  const choiceById = useMemo(() => new Map(choices.map((c) => [c.id, c])), [choices]);

  // Fit on open and whenever the container settles (dialog animation, rotation)
  // — until the technician moves the map themselves (pan, pinch or wheel; a tap
  // is not a move). The opening view frames the bays a vehicle can be pulled
  // into, centred, with a screen margin: parking spots are drawn as context but
  // never widen the frame (an unplaced spot sits at the floor's origin). Never
  // smaller than a usable tap target; when that means zooming in, the planned
  // (or selected, or first free) bay stays in view. "Whole floor" shows everything.
  const [view, setView] = useState<'bays' | 'floor'>('bays');
  const minZoom = useMemo(() => minTapZoom(layout, choices), [layout, choices]);
  const choiceRects = useMemo<MapRect[]>(() => {
    const ids = new Set(choices.map((c) => c.id));
    return layout.bays.filter((b) => ids.has(b.id)).map((b) => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
  }, [layout, choices]);
  const focus = useMemo(() => {
    const id = focusBayId ?? selectedBayId ?? choices.find((c) => !c.occupied && !c.refused)?.id ?? null;
    return layout.bays.find((b) => b.id === id) ?? null;
  }, [layout, choices, focusBayId, selectedBayId]);
  const fitRef = useRef(() => {});
  fitRef.current = () =>
    view === 'floor'
      ? fitToScreen({ padPx: FIT_MARGIN_PX })
      : fitToScreen({ rects: choiceRects, padPx: FIT_MARGIN_PX, minZoom, focus });

  useEffect(() => {
    camera.resetUserMoved();
    setView('bays');
    const raf = requestAnimationFrame(() => { if (!camera.hasUserMoved()) fitRef.current(); });
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return () => cancelAnimationFrame(raf);
    const ro = new ResizeObserver(() => { if (!camera.hasUserMoved()) fitRef.current(); });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refit only for a new floor
  }, [items, containerRef]);

  // The toggle is an explicit request for a fresh view: forget earlier panning.
  useEffect(() => {
    camera.resetUserMoved();
    fitRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the requested view changes
  }, [view]);

  const tap = (bayId: string) => {
    // A pan that ended on a bay is not a choice.
    if (camera.consumeDrag()) return;
    const c = choiceById.get(bayId);
    if (!onSelect || disabled || !c || c.occupied || c.refused) return;
    onSelect(bayId);
  };

  return (
    <div
      ref={containerRef}
      data-testid="facility-picker-map"
      role={onSelect ? 'radiogroup' : 'img'}
      aria-label={onSelect ? 'Bays on the shop floor' : 'Shop floor'}
      className={cn(
        'relative w-full overflow-hidden rounded-md border bg-muted/20 select-none',
        // Fixed, responsive height: most of a phone, bounded on a desktop.
        'h-[clamp(240px,55dvh,560px)]',
        className,
      )}
      style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
      onMouseDown={camera.handlers.onMouseDown}
      onTouchStart={camera.handlers.onTouchStart}
      onTouchMove={camera.handlers.onTouchMove}
      onTouchEnd={camera.handlers.onTouchEnd}
    >
      <div
        className="relative will-change-transform"
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
          width: `${bounds.width}px`,
          height: `${bounds.height}px`,
        }}
      >
        {layout.spots.map((s) => (
          <div
            key={`spot-${s.id}`}
            aria-hidden
            data-testid={`map-spot-${s.id}`}
            className="pointer-events-none absolute flex items-center justify-center rounded border border-dashed border-muted-foreground/40 text-xs text-muted-foreground"
            style={{ left: s.x - bounds.minX, top: s.y - bounds.minY, width: s.w, height: s.h }}
          >
            {s.label}
          </div>
        ))}

        {layout.bays.map((b) => {
          const c = choiceById.get(b.id);
          const style = { left: b.x - bounds.minX, top: b.y - bounds.minY, width: b.w, height: b.h };
          if (!c) {
            // On the floor, but not a bay a vehicle can be pulled into now.
            return (
              <div
                key={b.id}
                aria-hidden
                data-testid={`map-bay-${b.id}`}
                data-state="unavailable"
                className="pointer-events-none absolute flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/40 p-1 text-center text-muted-foreground opacity-60"
                style={style}
              >
                <span className="text-sm font-semibold">{b.label}</span>
                <span className="text-xs">Not available</span>
              </div>
            );
          }
          const state = mapBayState(c, selectedBayId);
          const unselectable = !onSelect || c.occupied || c.refused || disabled;
          return (
            <button
              key={b.id}
              type="button"
              role={onSelect ? 'radio' : undefined}
              aria-checked={onSelect ? selectedBayId === b.id : undefined}
              aria-disabled={unselectable || undefined}
              aria-label={`${c.label} — ${STATE_WORDS[state]}`}
              data-testid={`map-bay-${b.id}`}
              data-state={state}
              tabIndex={onSelect && !unselectable ? 0 : -1}
              onClick={() => tap(b.id)}
              className={cn(
                'absolute flex flex-col items-center justify-center gap-0.5 rounded-lg border-2 p-1 text-center',
                STATE_CLASS[state],
                !onSelect && 'cursor-default',
              )}
              style={style}
            >
              <span className="text-sm font-semibold leading-tight">{b.label}</span>
              <span className="text-xs leading-tight">{STATE_WORDS[state]}</span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        data-map-control
        data-testid="facility-picker-fit"
        aria-label={view === 'bays' ? 'Show the whole shop floor' : 'Fit the available bays'}
        onClick={() => {
          camera.resetUserMoved();
          if (view === 'bays') setView('floor');
          else if (view === 'floor') setView('bays');
        }}
        className="absolute bottom-2 right-2 flex h-11 w-11 items-center justify-center rounded-md border bg-background/90 shadow-sm"
      >
        {view === 'bays' ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
      </button>
    </div>
  );
}
